import React from 'react';
import { AuthPanel } from './auth/AuthPanel';
import { LEGAL_DOCUMENTS, legalUrl } from '../constants/onboarding';

/**
 * The signed-out screen — the first thing anyone sees.
 *
 * ⚠️ IT PAINTS ITS OWN DARK GROUND rather than using theme tokens, for the same
 * reason the onboarding sequence does: it is the only thing on screen, it is
 * dark in every theme, and the theme store has not been hydrated from the cloud
 * yet at this point — so reading tokens here would flash whatever the last
 * account on this device preferred.
 *
 * ⭐ Two panes at `lg`: the brand on the left, the form on the right. Below that
 * the brand collapses to a header, because a 390px column cannot carry both and
 * the form is what someone came here to use.
 */
export function LandingPage() {
  const terms = legalUrl(LEGAL_DOCUMENTS.terms);
  const privacy = legalUrl(LEGAL_DOCUMENTS.privacy);

  return (
    <div
      className="flex min-h-screen flex-col lg:flex-row"
      style={{ background: '#07070E', colorScheme: 'dark' }}
    >
      {/* ── Brand ─────────────────────────────────────────────────────────── */}
      {/* ⚠️ iOS runs edge to edge (`viewport-fit=cover`): without the safe-area insets the
          logo sat under the clock and the last line under the home bar (simulator, 2026-10-04). */}
      <div
        className="relative flex shrink-0 flex-col justify-between overflow-hidden px-7 pb-6 pt-[calc(2rem+var(--safe-area-top,env(safe-area-inset-top,0px)))] lg:w-[46%] lg:px-14 lg:pb-14 lg:pt-14"
        style={{
          background:
            'radial-gradient(ellipse 760px 620px at 30% 42%, rgba(167,57,226,.24) 0%, rgba(7,7,14,0) 68%), ' +
            'radial-gradient(ellipse 520px 420px at 62% 18%, rgba(60,194,221,.14) 0%, rgba(7,7,14,0) 70%), #07070E',
        }}
      >
        <div className="flex items-center gap-3">
          <img src="/The_Circle.png" alt="" className="h-9 w-9" />
          <span className="text-[19px] tracking-[.22em] text-white" style={{ fontFamily: DISPLAY }}>
            METANOIA
          </span>
        </div>

        {/* The orb, given room to be the thing it is. Hidden on a phone, where
            it would push the form below the fold for no gain. */}
        <div className="my-12 hidden justify-center lg:flex" aria-hidden="true">
          <img src="/The_Circle.png" alt="" className="h-[300px] w-[300px] opacity-95" />
        </div>

        <div className="mt-6 lg:mt-0">
          <p className="mb-2 text-[10px] uppercase tracking-[.24em] lg:text-[11px]" style={{ color: '#6B7280' }}>
            A personal development initiative
          </p>
          <h2
            className="text-[26px] font-light leading-[1.08] tracking-[-.02em] text-white lg:text-[42px]"
            style={{ fontFamily: DISPLAY }}
          >
            Design your becoming.
          </h2>
          <div
            className="my-4 h-px w-32 lg:my-6"
            style={{ background: 'linear-gradient(90deg, #3CC2DD, #A739E2 55%, #F58C56)' }}
          />
          {/* ⭐ Three lines, and the third is the argument the other two set up. */}
          <div className="flex flex-col gap-1 text-[13px] leading-relaxed lg:gap-1.5 lg:text-[15px]" style={{ color: '#9AA3B2' }}>
            <p className="m-0">Knowledge without action is wasted potential.</p>
            <p className="m-0">Action without self-regulation is unstable.</p>
            <p className="m-0 text-white">Growth requires both.</p>
          </div>
        </div>
      </div>

      {/* ── Form ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center px-7 pt-7 pb-[calc(1.75rem+env(safe-area-inset-bottom,0px))] lg:px-14 lg:py-12">
        <AuthPanel />

        {/* ⚠️ REAL LINKS. These were `<button>` elements with no handler — they
            looked like links, underlined on hover, and did nothing at all, on the
            one screen where someone is being asked to agree to them. They use the
            same `legalUrl` the consent step does, so an unpublished document is
            named in plain text rather than becoming a dead link. */}
        <p className="mt-7 max-w-[380px] lg:mt-10 text-center text-[13px] leading-relaxed" style={{ color: '#6B7280' }}>
          By continuing you agree to our{' '}
          <LegalLink href={terms} label={LEGAL_DOCUMENTS.terms.label} />{' '}
          and acknowledge that you have read our{' '}
          <LegalLink href={privacy} label={LEGAL_DOCUMENTS.privacy.label} />.
        </p>
      </div>
    </div>
  );
}

const DISPLAY = "'Brother 1816', Outfit, system-ui, -apple-system, sans-serif";

function LegalLink({ href, label }: { href: string | null; label: string }) {
  if (!href) return <span style={{ color: '#9AA3B2' }}>{label}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="underline underline-offset-4 transition-colors hover:text-white"
      style={{ color: '#8ADAEB' }}
    >
      {label}
    </a>
  );
}
